import React, { useEffect, useState, useCallback } from "react";
import { useSetUser } from "../contexts/UserContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import {
  Stack,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { markWelcomeTourExpectedAfterLogin } from "../utils/firstLoginOnboardingStorage";
import PublicMarketingShell from "../components/PublicMarketingShell";
import { PM_COLORS, publicCardSx } from "../constants/publicMarketingTheme";
import {
  EntityModalTransition,
  entityModalCancelButtonProps,
  entityModalDialogActionsSx,
  entityModalSubmitButtonProps,
  formFieldSecondaryFocusSx,
  getUnifiedModalPaperProps,
  getUnifiedModalTitleSx,
} from "../components/entityModalShell";

const RESEND_INTERVAL_SEC = 60;

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const setUser = useSetUser();
  const { showSnackbar } = useSnackbar();

  const [forgotOpen, setForgotOpen] = useState(false);
  const [fpUsername, setFpUsername] = useState("");
  const [fpEmailMasked, setFpEmailMasked] = useState(null);
  const [fpOtpExpiresSec, setFpOtpExpiresSec] = useState(null);
  const [fpOtp, setFpOtp] = useState("");
  const [fpPassword, setFpPassword] = useState("");
  const [fpConfirm, setFpConfirm] = useState("");
  const [fpShowPw, setFpShowPw] = useState(false);
  const [fpSending, setFpSending] = useState(false);
  const [fpVerifying, setFpVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [subscriptionRenewOpen, setSubscriptionRenewOpen] = useState(false);
  const [subscriptionRenewOrgId, setSubscriptionRenewOrgId] = useState(null);
  const [subscriptionRenewIntro, setSubscriptionRenewIntro] = useState("");
  const [renewCodeInput, setRenewCodeInput] = useState("");
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewError, setRenewError] = useState("");

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const t = setInterval(() => {
      setResendCountdown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCountdown]);

  const resetForgotForm = useCallback(() => {
    setFpUsername("");
    setFpEmailMasked(null);
    setFpOtpExpiresSec(null);
    setFpOtp("");
    setFpPassword("");
    setFpConfirm("");
    setCodeSent(false);
    setResendCountdown(0);
  }, []);

  const openForgot = () => {
    resetForgotForm();
    setForgotOpen(true);
  };

  const closeForgot = () => {
    setForgotOpen(false);
    resetForgotForm();
  };

  const closeSubscriptionRenew = () => {
    if (renewSubmitting) return;
    setSubscriptionRenewOpen(false);
    setSubscriptionRenewOrgId(null);
    setSubscriptionRenewIntro("");
    setRenewCodeInput("");
    setRenewError("");
  };

  const startResendCountdown = (seconds = RESEND_INTERVAL_SEC) => {
    setResendCountdown(Math.max(1, Math.min(RESEND_INTERVAL_SEC, seconds)));
  };

  const handleRequestOtp = async () => {
    const un = String(fpUsername || "").trim();
    if (!un) {
      showSnackbar({
        message: "Enter your username.",
        severity: "warning",
      });
      return;
    }
    setFpSending(true);
    try {
      const res = await axios.post(
        "/api/public/auth/forgot-password/request-otp",
        { username: un },
        { withCredentials: true },
      );
      if (res.data?.emailMasked) {
        setFpEmailMasked(String(res.data.emailMasked));
        setFpOtpExpiresSec(
          Number(res.data.expiresInSeconds) > 0
            ? Number(res.data.expiresInSeconds)
            : 60,
        );
        setCodeSent(true);
        startResendCountdown(RESEND_INTERVAL_SEC);
      } else {
        showSnackbar({
          message:
            res.data?.message ||
            "If this username exists, a code would be sent to the email on file.",
          severity: "info",
          duration: 7000,
        });
      }
    } catch (err) {
      const sec = err.response?.data?.secondsRemaining;
      if (err.response?.status === 429 && sec != null) {
        startResendCountdown(Number(sec) || RESEND_INTERVAL_SEC);
        showSnackbar({
          message:
            err.response?.data?.message ||
            "Please wait before requesting another code.",
          severity: "warning",
        });
        return;
      }
      if (err.response?.status === 400) {
        showSnackbar({
          message:
            err.response?.data?.message ||
            "Could not send a reset code for this address.",
          severity: "error",
          duration: 9000,
        });
        return;
      }
      showSnackbar({
        message:
          err.response?.data?.message ||
          "Could not send reset email. Try again later.",
        severity: "error",
      });
    } finally {
      setFpSending(false);
    }
  };

  const handleVerifyReset = async () => {
    const otpDigits = String(fpOtp || "").replace(/\D/g, "");
    if (otpDigits.length !== 6) {
      showSnackbar({
        message: "Enter the 6-digit code from your email.",
        severity: "warning",
      });
      return;
    }
    if (fpPassword !== fpConfirm) {
      showSnackbar({ message: "Passwords do not match.", severity: "warning" });
      return;
    }
    setFpVerifying(true);
    try {
      const res = await axios.post(
        "/api/public/auth/forgot-password/verify",
        {
          username: String(fpUsername || "").trim(),
          otp: otpDigits,
          newPassword: fpPassword,
        },
        { withCredentials: true },
      );
      showSnackbar({
        message: res.data?.message || "Password updated. You can sign in now.",
        severity: "success",
      });
      if (res.data?.username) {
        setUsername(String(res.data.username));
      }
      closeForgot();
    } catch (err) {
      showSnackbar({
        message: err.response?.data?.message || "Reset failed.",
        severity: "error",
        duration: 9000,
      });
    } finally {
      setFpVerifying(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { username, password };
      const res = await axios.post("/api/auth/login", body, {
        withCredentials: true,
      });
      localStorage.setItem("accessToken", "cookie");
      setUser(res.data.user);
      markWelcomeTourExpectedAfterLogin();
      navigate("/dashboard");
    } catch (err) {
      const data = err.response?.data;
      if (
        err.response?.status === 403 &&
        data?.code === "SUBSCRIPTION_EXPIRED" &&
        data?.organizationId != null
      ) {
        setSubscriptionRenewOrgId(Number(data.organizationId));
        setSubscriptionRenewIntro(String(data?.message || "").trim());
        setRenewCodeInput("");
        setRenewError("");
        setSubscriptionRenewOpen(true);
      } else {
        showSnackbar({
          message: data?.message || "Login failed",
          severity: "error",
          duration: 8000,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedeemSubscriptionAndLogin = async (e) => {
    e?.preventDefault();
    const trimmed = String(renewCodeInput || "").trim();
    if (!trimmed) {
      setRenewError("Enter your subscription renewal code.");
      return;
    }
    if (subscriptionRenewOrgId == null || !Number.isFinite(subscriptionRenewOrgId)) {
      setRenewError("Missing organization. Close this dialog and sign in again.");
      return;
    }
    setRenewSubmitting(true);
    setRenewError("");
    try {
      const res = await axios.post(
        "/api/auth/redeem-subscription-and-login",
        {
          username: String(username || "").trim(),
          password,
          organizationId: subscriptionRenewOrgId,
          code: trimmed.toUpperCase(),
        },
        { withCredentials: true },
      );
      localStorage.setItem("accessToken", "cookie");
      setUser(res.data.user);
      markWelcomeTourExpectedAfterLogin();
      closeSubscriptionRenew();
      showSnackbar({
        message: "Subscription renewed. Welcome back.",
        severity: "success",
      });
      navigate("/dashboard");
    } catch (err) {
      setRenewError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Could not apply code.",
      );
    } finally {
      setRenewSubmitting(false);
    }
  };

  return (
    <PublicMarketingShell
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        minHeight: "100%",
      }}
    >
      <>
      <Paper
        elevation={0}
        sx={{
          ...publicCardSx,
          width: "100%",
          maxWidth: 960,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 20px 44px rgba(0,0,0,0.35)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            minHeight: { xs: "auto", md: 560 },
          }}
        >
          <Box
            sx={{
              flex: "1 1 380px",
              p: { xs: 3, md: 5 },
              color: PM_COLORS.textPrimary,
              background:
                "radial-gradient(circle at top right, rgba(56,189,248,0.15), transparent 45%), rgba(15,23,42,0.8)",
            }}
          >
            <Chip
              label="Secure ERP Access"
              color="secondary"
              sx={{ fontWeight: 700, mb: 1.5 }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Welcome back to JD Integrations
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(226,232,240,0.9)" }}>
              Access your organization dashboard, manage module workflows, and
              continue where your team left off.
            </Typography>

            <Stack spacing={1.25} sx={{ mt: 3 }}>
              <Typography variant="body2">- Cookie-based secure authentication</Typography>
              <Typography variant="body2">- Role and permission aware access</Typography>
              <Typography variant="body2">- Real-time cross-module visibility</Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: "1 1 420px",
              p: { xs: 3, md: 5 },
              backgroundColor: "rgba(2,6,23,0.82)",
            }}
          >
            <Typography variant="h4" sx={{ color: "#f8fafc", fontWeight: 800 }}>
              Login
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(148,163,184,0.95)", mt: 0.75 }}>
              Sign in with your username or email and password. If several accounts share the
              same email, use your username.
            </Typography>
            <Divider sx={{ my: 2.2, borderColor: "rgba(148,163,184,0.28)" }} />
            <form onSubmit={handleLogin}>
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                color="secondary"
                autoComplete="username"
              />
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                color="secondary"
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                disabled={submitting}
                sx={{ mt: 2, py: 1.1, borderRadius: 2, fontWeight: 700 }}
              >
                {submitting ? "Signing in..." : "Login"}
              </Button>
            </form>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={openForgot}
                sx={{ color: "secondary.light", cursor: "pointer" }}
              >
                Forgot password?
              </Link>
            </Stack>
            <Button
              color="secondary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => navigate("/signup")}
            >
              Don&apos;t have an account? Sign Up
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={forgotOpen}
        onClose={closeForgot}
        fullWidth
        maxWidth="sm"
        TransitionComponent={EntityModalTransition}
        keepMounted
        PaperProps={getUnifiedModalPaperProps("Core")}
      >
        <DialogTitle sx={getUnifiedModalTitleSx("Core")}>Reset password</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your username. We will send a one-time code to the email address stored for
            that account. You can request another code after {RESEND_INTERVAL_SEC} seconds.
          </Typography>
          <Stack spacing={2} sx={formFieldSecondaryFocusSx}>
          <TextField
            label="Username"
            fullWidth
            margin="dense"
            value={fpUsername}
            onChange={(e) => setFpUsername(e.target.value)}
            color="secondary"
            autoComplete="username"
            disabled={fpSending || fpVerifying || Boolean(fpEmailMasked)}
            helperText={
              fpEmailMasked
                ? "Username is locked for this reset. Close the dialog to start over."
                : "Use the same username you use to sign in."
            }
          />
          {fpEmailMasked ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Verification code sent
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                A one-time code was sent to <strong>{fpEmailMasked}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                This code expires in {fpOtpExpiresSec ?? 60} seconds.
              </Typography>
            </Alert>
          ) : null}
          <Button
            {...entityModalSubmitButtonProps}
            fullWidth
            sx={{ mt: 0, py: 1, fontWeight: 700 }}
            onClick={handleRequestOtp}
            disabled={fpSending || fpVerifying || resendCountdown > 0}
          >
            {fpSending
              ? "Sending…"
              : resendCountdown > 0
                ? `Resend code in ${resendCountdown}s`
                : codeSent && fpEmailMasked
                  ? "Resend code"
                  : "Send verification code"}
          </Button>

          {codeSent && fpEmailMasked ? (
            <Stack spacing={1.5} sx={{ mt: 3 }}>
              <Divider />
              <TextField
                label="6-digit code"
                fullWidth
                margin="dense"
                value={fpOtp}
                onChange={(e) =>
                  setFpOtp(String(e.target.value).replace(/\D/g, "").slice(0, 6))
                }
                color="secondary"
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                disabled={fpVerifying}
              />
              <TextField
                label="New password"
                type={fpShowPw ? "text" : "password"}
                fullWidth
                margin="dense"
                value={fpPassword}
                onChange={(e) => setFpPassword(e.target.value)}
                color="secondary"
                autoComplete="new-password"
                disabled={fpVerifying}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setFpShowPw((v) => !v)}
                        aria-label="toggle new password visibility"
                      >
                        {fpShowPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm new password"
                type={fpShowPw ? "text" : "password"}
                fullWidth
                margin="dense"
                value={fpConfirm}
                onChange={(e) => setFpConfirm(e.target.value)}
                color="secondary"
                autoComplete="new-password"
                disabled={fpVerifying}
              />
            </Stack>
          ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={entityModalDialogActionsSx}>
          <Button
            {...entityModalCancelButtonProps}
            onClick={closeForgot}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          {codeSent && fpEmailMasked ? (
            <Button
              {...entityModalSubmitButtonProps}
              onClick={handleVerifyReset}
              disabled={fpVerifying}
              sx={{ fontWeight: 700 }}
            >
              {fpVerifying ? "Updating…" : "Set new password"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={subscriptionRenewOpen}
        onClose={closeSubscriptionRenew}
        fullWidth
        maxWidth="sm"
        TransitionComponent={EntityModalTransition}
        keepMounted
        PaperProps={getUnifiedModalPaperProps("Core")}
      >
        <DialogTitle sx={getUnifiedModalTitleSx("Core")}>
          Renew subscription
        </DialogTitle>
        <DialogContent dividers>
          {subscriptionRenewIntro ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {subscriptionRenewIntro}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the subscription renewal code you received from your account manager or
            platform support. After it is applied, you will be signed in automatically.
          </Typography>
          <Stack
            component="form"
            id="login-subscription-renew-form"
            spacing={2}
            onSubmit={handleRedeemSubscriptionAndLogin}
            sx={formFieldSecondaryFocusSx}
          >
            <TextField
              label="Subscription renewal code"
              fullWidth
              margin="dense"
              value={renewCodeInput}
              onChange={(e) => setRenewCodeInput(String(e.target.value).toUpperCase())}
              color="secondary"
              autoComplete="off"
              disabled={renewSubmitting}
              error={Boolean(renewError)}
              helperText={renewError || "Codes are not case-sensitive."}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={entityModalDialogActionsSx}>
          <Button
            {...entityModalCancelButtonProps}
            onClick={closeSubscriptionRenew}
            disabled={renewSubmitting}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            {...entityModalSubmitButtonProps}
            type="submit"
            form="login-subscription-renew-form"
            disabled={renewSubmitting}
            sx={{ fontWeight: 700 }}
          >
            {renewSubmitting ? "Applying…" : "Apply code and sign in"}
          </Button>
        </DialogActions>
      </Dialog>
      </>
    </PublicMarketingShell>
  );
};

export default LoginPage;
